import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Stack, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, TextField, Tooltip, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, InputAdornment,
} from '@mui/material';
import {
  CheckOutlined, CloseOutlined, ForwardOutlined, SearchOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, meApi } from '../../api';
import type { MeColleague, MeDecidedApproval } from '../../api/me';
import { ColleaguePickerSingle } from '../../components/worker/ColleaguePicker';
import { useTranslation } from 'react-i18next';
import { activeLocale, fmtAge } from '../../utils/datetime';
import WorkerListToolbar from '../../components/worker/WorkerListToolbar';
import { translateApiError } from '../../i18n/translateApiError';
import { seedStatusFromUrl } from '../../utils/seedStatusFromUrl';

import { getSystemRequestTitle, getSystemTaskTitle } from '../Processes/processDisplay';

interface ApprovalRow {
  id: string;
  title: string;
  /** The definition's form schema; its `system_key` says whether `title` is a built-in's (localized). */
  form_schema?: unknown;
  requester_id: string;
  current_approver_id?: string | null;
  status: string;
  priority: string;
  submitted_at?: string | null;
  due_date?: string | null;
  created_at: string;
}

type ActionKind = 'approve' | 'reject' | 'forward' | null;

function priorityColor(p: string): 'default' | 'warning' | 'error' | 'info' {
  switch (p) {
    case 'critical': return 'error';
    case 'high': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
}

// Localized units (`5小时` / `3 日`), not English `5h` / `3d`.
const ageLabel = (iso?: string | null): string => fmtAge(iso);

const WorkerApprovals: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<ApprovalRow | null>(null);
  const [action, setAction] = useState<ActionKind>(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [delegateTo, setDelegateTo] = useState<MeColleague | null>(null);
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  // Filters (owner 2026-06-28: My Approvals had no filtering). The inbox is
  // pending-only, so the useful dimensions are priority (triage) + a title
  // search.
  const [query, setQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  // SCOPE, not a filter (owner audit 2026-09-03: My Approvals 里 attendance 有状态过滤,
  // processs 里没有). The attendance tab's Pending / Reviewed switches which QUERY runs, and
  // this had no equivalent at all: a worker could show what they decided on a leave request
  // and not on a process one, inside one entry that is supposed to behave one way.
  //
  // It cannot be a client-side filter over the inbox. The inbox is scoped to the CURRENT
  // approver, so a request I decided has advanced past me and is simply not in the response.
  // `/me/approvals/decided` reads the task I actioned instead.
    /**
   * The status filter can be SEEDED from `?status=`, because a click on a COUNT must land on the list
   * that count describes. The Overview's pending card links here with `?status=pending`; the sidebar
   * entry links without it and keeps this page's own default.
   *
   * Seed only — the toolbar owns the value afterwards. Syncing both ways would make the URL and the
   * control two sources of truth for one piece of state (svdy-rules §10.5b), and the `?compose=1`
   * precedent in this repo consumes-and-clears for the same reason.
   */
  const [scope, setScope] = useState<'pending' | 'reviewed'>(() => {
    const seeded = seedStatusFromUrl<'pending' | 'reviewed'>('pending');
    return seeded;
  });
  const [decided, setDecided] = useState<MeDecidedApproval[]>([]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) =>
      (priorityFilter === 'all' || r.priority === priorityFilter) &&
      (!q || (r.title ?? '').toLowerCase().includes(q)),
    );
  }, [rows, query, priorityFilter]);

  const priorityOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.priority).filter(Boolean))),
    [rows],
  );

  const fetch = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      if (scope === 'reviewed') {
        const data = await meApi.approvals.decided();
        setDecided(Array.isArray(data) ? data : []);
      } else {
        const data = (await meApi.approvals.inbox()) as ApprovalRow[];
        setRows(Array.isArray(data) ? data : []);
      }
    } catch {
      // Preserve last-known-good approvals; an API failure is not an empty inbox.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAction = (row: ApprovalRow, kind: ActionKind) => {
    setSelected(row);
    setAction(kind);
    setNotes('');
    setReason('');
    setDelegateTo(null);
    setJustification('');
  };

  const closeAction = () => {
    if (saving) return;
    setSelected(null);
    setAction(null);
  };

  const handleSubmit = async () => {
    if (!selected || !action) return;
    setSaving(true);
    try {
      if (action === 'approve') {
        await meApi.approvals.approve(selected.id, { notes: notes || undefined });
      } else if (action === 'reject') {
        if (!reason.trim()) throw new ApiError(400, 'Reason is required');
        await meApi.approvals.reject(selected.id, { reason: reason.trim() });
      } else if (action === 'forward') {
        if (!delegateTo) throw new ApiError(400, 'Pick a colleague to forward to');
        await meApi.approvals.delegate(selected.id, {
          delegated_to: delegateTo.worker_id,
          justification: justification || undefined,
        });
      }
      const successKey = action === 'approve'
        ? 'worker:approvals.toastApproved'
        : action === 'reject'
          ? 'worker:approvals.toastRejected'
          : 'worker:approvals.toastForwarded';
      setToast({ msg: t(successKey), severity: 'success' });
      setSelected(null);
      setAction(null);
      await fetch();
    } catch (e) {
      setToast({
        msg: e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const emptyForScope = scope === 'reviewed' ? decided.length === 0 : rows.length === 0;
  if (loading && emptyForScope) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: embedded ? 0 : '12px', width: '100%' }}>
      {!embedded && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('worker:approvals.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('worker:approvals.pendingSubtitle')}</Typography>
        </Box>
      )}
      {loadError && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 1.5 }}
          action={<Button color="inherit" size="small" onClick={fetch}>{t('common:actions.retry')}</Button>}
        >
          {t('common:feedback.loadFailed')}
        </Alert>
      )}
      {/* The scope switch, on the shared toolbar so its position matches every other list.
          Search rides along here rather than in a second row further down, which is where this
          page used to keep it — behind `rows.length === 0`, so an empty list looked unfiltered. */}
      <WorkerListToolbar
        set="decided"
        value={scope}
        onChange={(v) => setScope(v as 'pending' | 'reviewed')}
        search={query}
        onSearchChange={setQuery}
      />

      {scope === 'reviewed' ? (
        decided.length === 0 && !loadError ? (
          <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">{t('worker:approvals.noDecided')}</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              {t('worker:approvals.noDecidedHint')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
                <TableRow>
                  <TableCell>{t('worker:approvals.colTitle')}</TableCell>
                  <TableCell>{t('worker:approvals.colRequester')}</TableCell>
                  <TableCell>{t('worker:approvals.colDecision')}</TableCell>
                  <TableCell>{t('worker:approvals.colDecidedAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {decided.map((d) => (
                  <TableRow
                    key={d.task_id}
                    hover={!!d.instance_id}
                    sx={{ cursor: d.instance_id ? 'pointer' : 'default' }}
                    // The specific record, not a list: a row whose whole affordance is being
                    // clickable must land somewhere the worker can actually open
                    // (svdy-ui §12.10). Instances without one are not clickable at all rather
                    // than clickable-and-dead.
                    onClick={d.instance_id ? () => navigate(`/me/requests/${d.request_id ?? ''}`) : undefined}
                  >
                    <TableCell>
                      {d.request_id
                        ? getSystemRequestTitle(t, d, d.title)
                        : getSystemTaskTitle(t, d, d.node_id, d.title)}
                    </TableCell>
                    <TableCell>{d.requester_name ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`worker:approvals.decision_${d.action_taken}`, { defaultValue: d.action_taken })}
                        color={d.action_taken === 'approved' ? 'success' : d.action_taken === 'rejected' ? 'error' : 'default'}
                        sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>{d.action_at ? new Date(d.action_at).toLocaleString(activeLocale()) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : rows.length === 0 && !loadError ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">{t('worker:approvals.noPending')}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('worker:approvals.noPendingHint')}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Filter bar — priority triage + title search. */}
          <Stack direction="row" spacing={1.5} sx={{ width: '100%', mb: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={t('worker:approvals.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ width: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
            />
            {priorityOptions.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>{t('worker:approvals.colPriority')}</InputLabel>
                <Select value={priorityFilter} label={t('worker:approvals.colPriority')} onChange={(e) => setPriorityFilter(e.target.value)}>
                  <MenuItem value="all">{t('worker:approvals.allPriorities')}</MenuItem>
                  {priorityOptions.map((p) => (
                    <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          {filtered.length === 0 ? (
            <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{t('worker:approvals.noMatch')}</Typography>
            </Paper>
          ) : (
          <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.75rem' } }}>
                <TableRow>
                  <TableCell>{t('worker:approvals.colTitle')}</TableCell>
                  <TableCell>{t('worker:approvals.colPriority')}</TableCell>
                  <TableCell>{t('worker:approvals.colSubmitted')}</TableCell>
                  <TableCell>{t('worker:approvals.colAge')}</TableCell>
                  <TableCell align="right">{t('worker:approvals.colActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/me/requests/${r.id}`)}
                  >
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 500 }}>{getSystemRequestTitle(t, r, r.title)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={r.priority}
                        size="small"
                        color={priorityColor(r.priority)}
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell><Typography variant="caption">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString(activeLocale()) : '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{ageLabel(r.submitted_at ?? r.created_at)}</Typography></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t('worker:approvals.tooltipApprove')}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => { e.stopPropagation(); openAction(r, 'approve'); }}
                          >
                            <CheckOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('worker:approvals.tooltipReject')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); openAction(r, 'reject'); }}
                          >
                            <CloseOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('worker:approvals.tooltipForward')}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openAction(r, 'forward'); }}
                          >
                            <ForwardOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </>
      )}

      <Dialog open={!!action} onClose={closeAction} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, textTransform: 'capitalize' }}>
          {action} — {getSystemRequestTitle(t, selected, selected?.title)}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {action === 'approve' && (
              <TextField
                label={t('worker:approvals.labelNotes')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                size="small"
                multiline
                minRows={3}
                fullWidth
              />
            )}
            {action === 'reject' && (
              <TextField
                label={t('worker:approvals.labelReason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                multiline
                minRows={3}
                required
                fullWidth
                helperText={t('worker:approvals.helperReject')}
              />
            )}
            {action === 'forward' && (
              <>
                <ColleaguePickerSingle
                  value={delegateTo}
                  onChange={setDelegateTo}
                  label={t('worker:approvals.labelForwardTo')}
                  placeholder={t('worker:approvals.placeholderColleague')}
                  required
                  helperText={t('worker:approvals.helperForward')}
                  excludeIds={
                    selected?.current_approver_id
                      ? [selected.current_approver_id]
                      : undefined
                  }
                />
                <TextField
                  label={t('worker:approvals.labelReason')}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  size="small"
                  multiline
                  minRows={2}
                  required
                  fullWidth
                  helperText={t('worker:approvals.helperForwardReason')}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} size="small" disabled={saving} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            disabled={saving}
            color={action === 'reject' ? 'error' : 'primary'}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            {saving ? t('worker:approvals.submitting') : t('common:actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WorkerApprovals;
